/**
 * Tasks Store
 *
 * Zustand store for task state management.
 * Fetches tasks from the API and handles WebSocket updates.
 */

import { create } from 'zustand';
import { tasksAPI } from '@/services/api';
import type { Task, TaskStatus } from '@/types';

// Re-export Task type for convenience
export type { Task } from '@/types';

// Extended task with team info for queue management
export interface QueuedTask extends Task {
  teamId: string;
  teamName: string;
  title?: string;
}

interface TasksStore {
  tasks: QueuedTask[];
  loading: boolean;
  error: string | null;

  // Fetch tasks from API
  fetchTasks: () => Promise<void>;

  // Get all tasks assigned to an operator
  getOperatorTasks: (operatorId: string) => QueuedTask[];

  // Get pending/in-progress tasks for an operator (excludes completed/cancelled)
  getOperatorPendingTasks: (operatorId: string) => QueuedTask[];

  // Return tasks to team queue (called on retirement)
  returnTasksToQueue: (operatorId: string) => QueuedTask[];

  // Assign task to operator
  assignTask: (taskId: string, operatorId: string, operatorName: string) => void;

  // Update task status
  updateTaskStatus: (taskId: string, status: TaskStatus) => void;

  // Get tasks in team queue (unassigned)
  getTeamQueuedTasks: (teamId: string) => QueuedTask[];

  // Execute task via API (fires background execution)
  executeTask: (taskId: string, operatorId: string) => Promise<void>;

  // Create task via API
  createTask: (data: { type: string; description: string; operatorId?: string; priority?: string }) => Promise<void>;

  // Handle WebSocket task update (merge single task update into store)
  handleTaskUpdate: (data: { taskId: string; status: string; [key: string]: unknown }) => void;
}

export const useTasksStore = create<TasksStore>((set, get) => ({
  tasks: [],
  loading: false,
  error: null,

  fetchTasks: async () => {
    set({ loading: true, error: null });
    try {
      const data = await tasksAPI.list();
      const tasks: QueuedTask[] = (data || []).map((t: any) => ({
        id: t.id,
        type: t.type,
        title: t.title,
        description: t.description,
        status: t.status,
        priority: t.priority || 'medium',
        operatorId: t.operatorId,
        operatorName: t.operatorName,
        teamId: t.teamId || '',
        teamName: t.teamName || '',
        progress: t.progress,
        deadline: t.deadline,
        createdAt: t.createdAt,
        startedAt: t.startedAt,
        completedAt: t.completedAt,
      }));
      set({ tasks, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  getOperatorTasks: (operatorId: string) => {
    return get().tasks.filter(task => task.operatorId === operatorId);
  },

  getOperatorPendingTasks: (operatorId: string) => {
    return get().tasks.filter(
      task =>
        task.operatorId === operatorId &&
        (task.status === 'pending' || task.status === 'in_progress' || task.status === 'needs_approval')
    );
  },

  returnTasksToQueue: (operatorId: string) => {
    const pendingTasks = get().getOperatorPendingTasks(operatorId);

    if (pendingTasks.length === 0) {
      return [];
    }

    set((state) => ({
      tasks: state.tasks.map(task => {
        if (task.operatorId === operatorId && (task.status === 'pending' || task.status === 'in_progress')) {
          return {
            ...task,
            operatorId: undefined,
            operatorName: undefined,
            status: 'pending' as TaskStatus,
            progress: undefined,
            startedAt: undefined,
          };
        }
        return task;
      }),
    }));

    return pendingTasks;
  },

  assignTask: (taskId: string, operatorId: string, operatorName: string) => {
    set((state) => ({
      tasks: state.tasks.map(task =>
        task.id === taskId
          ? { ...task, operatorId, operatorName, status: 'pending' as TaskStatus }
          : task
      ),
    }));
  },

  updateTaskStatus: (taskId: string, status: TaskStatus) => {
    set((state) => ({
      tasks: state.tasks.map(task =>
        task.id === taskId
          ? {
              ...task,
              status,
              startedAt: status === 'in_progress' && !task.startedAt ? new Date().toISOString() : task.startedAt,
              completedAt: status === 'completed' ? new Date().toISOString() : task.completedAt,
            }
          : task
      ),
    }));
  },

  getTeamQueuedTasks: (teamId: string) => {
    return get().tasks.filter(task => task.teamId === teamId && !task.operatorId);
  },

  executeTask: async (taskId: string, operatorId: string) => {
    try {
      await tasksAPI.execute(taskId, operatorId);
      // Optimistically update status — WebSocket will confirm
      get().updateTaskStatus(taskId, 'in_progress');
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  createTask: async (data) => {
    try {
      const result = await tasksAPI.create(data);
      // If operatorId was included, task auto-executes server-side (returns in_progress)
      // Refetch to get server-assigned IDs and fields
      await get().fetchTasks();
      return result;
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  handleTaskUpdate: (data: { taskId: string; status: string; progress?: number; detail?: string; [key: string]: unknown }) => {
    set((state) => {
      const existing = state.tasks.find(t => t.id === data.taskId);
      if (existing) {
        return {
          tasks: state.tasks.map(t =>
            t.id === data.taskId
              ? {
                  ...t,
                  status: data.status as TaskStatus,
                  completedAt: data.status === 'completed' ? new Date().toISOString() : t.completedAt,
                  progress: data.status === 'completed' ? 100
                    : data.status === 'failed' ? t.progress
                    : (data.progress ?? t.progress),
                }
              : t
          ),
        };
      }
      // Unknown task — refetch all
      get().fetchTasks();
      return state;
    });
  },
}));

/**
 * Get pending tasks for an operator (non-hook version)
 */
export function getOperatorPendingTasks(operatorId: string): QueuedTask[] {
  return useTasksStore.getState().getOperatorPendingTasks(operatorId);
}
